import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/base/PageMeta';
import { categories, articles, type Category } from '@/mocks/articles';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat?.name ?? '分类';
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>(slug ?? 'guide');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);

  const switchCategory = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const categoryMetaTitle = `${getCategoryName(activeCategory)} - 98堂 入口`;
  const categoryMetaDesc = currentCategory?.description ?? '查看98堂平台相关文章';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://98tang.com/' },
          { '@type': 'ListItem', position: 2, name: getCategoryName(activeCategory) },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${getCategoryName(activeCategory)} - 98堂 入口`,
        description: categoryMetaDesc,
        url: `https://98tang.com/category/${activeCategory}`,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={categoryMetaTitle}
        description={categoryMetaDesc}
        canonicalUrl={`https://98tang.com/category/${activeCategory}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-secondary-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: getCategoryName(activeCategory) },
              ]}
            />
          </div>
        </div>

        <div className="bg-secondary-50 border-b border-secondary-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {getCategoryName(activeCategory)}
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-secondary-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => switchCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-secondary-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block p-5 rounded-lg bg-background-50 border border-secondary-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                      {currentCategory?.name}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-4 pt-3 border-t border-secondary-100">
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {article.views.toLocaleString()}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line" />
                      {article.readTime}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-300" />
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}